// Admin — 推薦收益提領申請管理（2026-06-07）
// 列表 + 狀態/關鍵字篩選 + detail 對話框（依進度切狀態）+ 最低提領金額設定
import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Divider, Box, Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const PAGE_SIZE = 20;

const apiCall = (url, method, body, token) =>
  fetch(url, {
    method: method || "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const STATUS_META = {
  PENDING: { label: "收益審核中", color: "warning" },
  PROCESSING: { label: "收益發放中", color: "info" },
  COMPLETED: { label: "收益完成發放", color: "success" },
  REJECTED: { label: "已拒絕", color: "error" },
  CANCELED: { label: "已取消", color: "default" },
};

const STATUS_FILTERS = [
  { value: "ALL", label: "全部狀態" },
  { value: "PENDING", label: "收益審核中" },
  { value: "PROCESSING", label: "收益發放中" },
  { value: "COMPLETED", label: "收益完成發放" },
  { value: "REJECTED", label: "已拒絕" },
  { value: "CANCELED", label: "已取消" },
];

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

function ReferralWithdrawalsAdmin() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const [minAmount, setMinAmount] = useState("");
  const [minAmountInput, setMinAmountInput] = useState("");

  const [detail, setDetail] = useState(null); // 開啟的提領 detail
  const [actionStatus, setActionStatus] = useState(""); // 將切換到的狀態
  const [adminNote, setAdminNote] = useState("");
  const [transactionRef, setTransactionRef] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [saving, setSaving] = useState(false);

  // ── fetch ──
  const fetchList = useCallback(() => {
    const qs = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(PAGE_SIZE),
    });
    if (statusFilter && statusFilter !== "ALL") qs.set("status", statusFilter);
    if (keyword) qs.set("keyword", keyword);
    apiCall(`/api/admin/referral-withdrawals?${qs.toString()}`, "GET", null, token)
      .then((d) => {
        setRows(Array.isArray(d?.records) ? d.records : []);
        setTotal(d?.total || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      });
  }, [page, statusFilter, keyword, token]);

  const fetchSettings = useCallback(() => {
    apiCall("/api/admin/referral-withdrawals/settings", "GET", null, token)
      .then((d) => {
        setMinAmount(d?.minAmount ?? "");
        setMinAmountInput(String(d?.minAmount ?? ""));
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // ── min amount ──
  const saveMinAmount = async () => {
    const v = Number(minAmountInput);
    if (!Number.isFinite(v) || v < 0) { window.alert("請輸入有效金額"); return; }
    await apiCall("/api/admin/referral-withdrawals/settings", "POST", { minAmount: v }, token);
    fetchSettings();
  };

  // ── detail dialog ──
  const openDetail = async (id) => {
    const d = await apiCall(`/api/admin/referral-withdrawals/${id}`, "GET", null, token);
    setDetail(d);
    setActionStatus("");
    setAdminNote(d?.adminNote || "");
    setTransactionRef(d?.transactionRef || "");
    setRejectReason("");
  };

  const closeDetail = () => { setDetail(null); setActionStatus(""); };

  // 依現狀提供可切換的下一步
  const nextActions = (status) => {
    if (status === "PENDING") return [
      { value: "PROCESSING", label: "審核通過（→ 發放中）", color: "info" },
      { value: "REJECTED", label: "拒絕", color: "error" },
    ];
    if (status === "PROCESSING") return [
      { value: "COMPLETED", label: "確認已撥款（→ 完成）", color: "success" },
      { value: "REJECTED", label: "拒絕", color: "error" },
    ];
    return [];
  };

  const submitStatus = async () => {
    if (!actionStatus) return;
    if (actionStatus === "REJECTED" && !rejectReason.trim()) {
      window.alert("拒絕需填寫原因");
      return;
    }
    setSaving(true);
    try {
      const body = { status: actionStatus, adminNote };
      if (actionStatus === "COMPLETED") body.transactionRef = transactionRef;
      if (actionStatus === "REJECTED") body.rejectReason = rejectReason;
      const res = await apiCall(
        `/api/admin/referral-withdrawals/${detail.id}/status`,
        "POST",
        body,
        token
      );
      if (res?.id) {
        setDetail(res);
        setActionStatus("");
        fetchList();
      } else {
        window.alert(res?.message || "更新失敗");
      }
    } catch (e) {
      window.alert("更新失敗");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* 最低提領金額設定 */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <MDTypography variant="h6">最低提領金額 (USD)</MDTypography>
              <MDTypography variant="caption" color="text">
                目前設定：{minAmount === "" ? "—" : `$${minAmount}`}
              </MDTypography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <MDInput
                type="number"
                value={minAmountInput}
                onChange={(e) => setMinAmountInput(e.target.value)}
                size="small"
                sx={{ width: 140 }}
              />
              <MDButton variant="gradient" color="info" size="small" onClick={saveMinAmount}>
                儲存
              </MDButton>
            </Stack>
          </MDBox>
        </Card>

        {/* 列表 */}
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDTypography variant="h6" color="white">推薦收益提領申請</MDTypography>
            <MDBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
              <TextField
                select
                size="small"
                label="狀態"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{
                  minWidth: 170,
                  borderRadius: 1,
                  "& .MuiInputBase-input": { color: COLORS.white },
                  "& .MuiSelect-icon": { color: COLORS.white },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.9)" },
                  "& fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                  "&:hover fieldset": { borderColor: "#fff !important" },
                }}
                InputLabelProps={{ shrink: true }}
              >
                {STATUS_FILTERS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
              <MDInput
                placeholder="搜尋使用者 Email / 名稱"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setKeyword(keywordInput); setPage(0); } }}
                size="small"
                sx={{
                  minWidth: 260,
                  borderRadius: 1,
                  "& .MuiInputBase-input": { color: COLORS.white },
                  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.85)", opacity: 1 },
                  "& fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                  "&:hover fieldset": { borderColor: "#fff !important" },
                }}
              />
              <MDButton
                variant="contained"
                color="white"
                size="small"
                onClick={() => { setKeyword(keywordInput); setPage(0); }}
                sx={{ color: "#F36D26 !important", fontWeight: 700, px: 2.5, boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
              >
                篩選
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox pt={3} px={2} pb={2}>
            <TableContainer sx={{ boxShadow: "none" }}>
              <Table>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>申請時間</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>使用者</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">金額 (USD)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>狀態</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        目前尚無提領申請
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => {
                      const meta = STATUS_META[r.status] || STATUS_META.PENDING;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{fmt(r.createdAt)}</TableCell>
                          <TableCell>
                            <MDTypography variant="button" fontWeight="medium" display="block">
                              {r.user?.fullName || "-"}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              {r.user?.email || "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            ${Number(r.amount).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <MDButton size="small" variant="outlined" color="info" onClick={() => openDetail(r.id)}>
                              查看 / 處理
                            </MDButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* pager */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <MDTypography variant="caption" color="text">共 {total} 筆</MDTypography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <MDTypography variant="caption">{page + 1} / {totalPages}</MDTypography>
                <IconButton size="small" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* detail dialog */}
      <Dialog open={!!detail} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle>提領申請明細</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          {detail && (
            <Box display="flex" flexDirection="column" gap={1.5} pt={1}>
              <Row label="使用者" value={`${detail.user?.fullName || "-"}（${detail.user?.email || "-"}）`} />
              <Row label="金額" value={`$${Number(detail.amount).toFixed(2)} ${detail.currency || "USD"}`} />
              <Row
                label="目前狀態"
                value={
                  <Chip
                    label={(STATUS_META[detail.status] || {}).label || detail.status}
                    color={(STATUS_META[detail.status] || {}).color || "default"}
                    size="small"
                  />
                }
              />
              <Divider />
              <Row label="收款方式" value={detail.payoutMethod || "PAYPAL"} />
              <Row label="收款帳號" value={detail.payoutAccount || "-"} />
              <Row label="國家" value={detail.country || "-"} />
              <Row label="手機" value={`${detail.phoneCountry || ""} ${detail.phoneNumber || "-"}`} />
              <Divider />
              <Row label="申請時間" value={fmt(detail.createdAt)} />
              <Row label="審核時間" value={fmt(detail.reviewedAt)} />
              <Row label="撥款時間" value={fmt(detail.paidAt)} />
              {detail.transactionRef && <Row label="交易序號" value={detail.transactionRef} />}
              {detail.rejectReason && <Row label="拒絕原因" value={detail.rejectReason} />}

              {/* 操作區 */}
              {nextActions(detail.status).length > 0 && (
                <>
                  <Divider />
                  <MDTypography variant="button" fontWeight="medium">變更狀態</MDTypography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {nextActions(detail.status).map((a) => (
                      <MDButton
                        key={a.value}
                        size="small"
                        variant={actionStatus === a.value ? "gradient" : "outlined"}
                        color={a.color}
                        onClick={() => setActionStatus(a.value)}
                      >
                        {a.label}
                      </MDButton>
                    ))}
                  </Stack>

                  {actionStatus === "COMPLETED" && (
                    <TextField
                      label="交易序號 / 撥款備註"
                      value={transactionRef}
                      onChange={(e) => setTransactionRef(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  )}
                  {actionStatus === "REJECTED" && (
                    <TextField
                      label="拒絕原因（必填）"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  )}
                  {actionStatus && (
                    <TextField
                      label="管理備註（選填）"
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={closeDetail} color="secondary">關閉</MDButton>
          {detail && nextActions(detail.status).length > 0 && (
            <MDButton
              onClick={submitStatus}
              color="info"
              variant="gradient"
              disabled={!actionStatus || saving}
            >
              {saving ? "處理中..." : "確認變更"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

// 小工具：detail 一列
function Row({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
      <MDTypography variant="caption" color="text" sx={{ minWidth: 80 }}>{label}</MDTypography>
      <Box sx={{ textAlign: "right", wordBreak: "break-all" }}>
        {typeof value === "string" ? (
          <MDTypography variant="button" fontWeight="medium">{value}</MDTypography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}

export default ReferralWithdrawalsAdmin;
